/**
 * Phone verification gate for private tasks.
 *
 * Surfaces on top of `TaskPage` when `resolveTaskForWeb` returns
 * `auth_required`: the link was generated with phone-gating on, and
 * we need to be sure whoever is opening it actually owns the phone
 * number it was assigned to before showing the task body.
 *
 * White execute shell: X9 logo, country picker + tel input, OTP
 * verify, and LoadingScreen while the credential is being linked.
 *
 * Internal sub-states: `phone_input` → `code_input` → optional `terms`
 * (new accounts) → parent `onVerified`. Returning users with
 * `termsAcceptedAt` skip the Terms gate.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import FloatingFooter, {
  FloatingFooterCta,
  FloatingFooterNavButton,
} from '@/components/FloatingFooter';
import X9Logo from '../components/X9Logo';
import {
  initRecaptcha,
  clearRecaptcha,
  sendVerificationCode,
  verifyCodeAndLink,
  PhoneAuthError,
  describePhoneAuthError,
} from '../services/phoneAuth';
import { ensureContactForPhoneAuth } from '../services/api';
import { hasVerifiedPhoneOnSession, ensureSessionReady } from '@/services/auth';
import TermsGate from '@/components/TermsGate';
import { getFirebaseAuth } from '@/config/firebase';
import { continueIfReturning } from '@/services/userProfile';
import {
  COUNTRY_OPTIONS,
  detectDialCode,
  toE164,
  phoneInputProblem,
  type PhoneInputProblem,
} from '../services/countryCode';
import type { ConfirmationResult } from 'firebase/auth';
import LoadingScreen from '@/components/LoadingScreen';

interface Props {
  onVerified: (meta?: { priorAnonymousUid?: string }) => void;
  phoneVerified?: boolean;
  mode?: 'gate' | 'post_accept';
  onSkip?: () => void;
  /** Keep the number form even if this browser already has a verified phone. */
  forceInput?: boolean;
}

type Stage = 'phone_input' | 'code_input' | 'terms' | 'verifying';

// Namespaced container id so the invisible reCAPTCHA widget can't
// collide with anything else on the page. Mirrors the convention
// used by capture's SuccessStep / PhoneStep.
const RECAPTCHA_CONTAINER_ID = 'x9-do-phone-recaptcha';

const PHONE_PROBLEM_MESSAGE: Record<PhoneInputProblem, string> = {
  too_short: 'Please enter a valid phone number.',
  missing_area_code: 'Include the area code — for example 11 for São Paulo.',
  landline: 'This looks like a landline. We need a mobile number to text you.',
  not_mobile: "This doesn't look like a mobile number. Please check it.",
};

export default function PhoneVerifyStep({
  onVerified,
  phoneVerified = false,
  mode = 'gate',
  onSkip,
  forceInput = false,
}: Props) {
  const [stage, setStage] = useState<Stage>(
    phoneVerified ? 'verifying' : 'phone_input'
  );
  const [dialCode, setDialCode] = useState('+55');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [confirmation, setConfirmation] = useState<ConfirmationResult | null>(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const priorAnonymousUidRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (forceInput) return;
    if (phoneVerified) {
      onVerified();
      return;
    }
    let cancelled = false;
    void hasVerifiedPhoneOnSession().then(async (verified) => {
      if (cancelled || !verified) return;
      const user = getFirebaseAuth().currentUser;
      if (user && (await continueIfReturning(user.uid))) {
        if (!cancelled) {
          setStage('verifying');
          onVerified();
        }
        return;
      }
      if (!cancelled) setStage('terms');
    });
    return () => {
      cancelled = true;
    };
  }, [forceInput, phoneVerified, onVerified]);

  useEffect(() => {
    detectDialCode().then(setDialCode);
    initRecaptcha(RECAPTCHA_CONTAINER_ID);
    return () => clearRecaptcha();
  }, []);

  const handleSendCode = useCallback(async () => {
    // Naming the specific mistake matters more here than anywhere else: a
    // number that is wrong in a Brazilian-specific way sends successfully and
    // then nothing arrives, leaving the visitor with no idea whether to wait.
    const problem = phoneInputProblem(dialCode, phone);
    if (problem) {
      setError(PHONE_PROBLEM_MESSAGE[problem]);
      return;
    }
    setError('');
    setSending(true);
    try {
      const e164 = toE164(dialCode, phone);
      const result = await sendVerificationCode(e164);
      setConfirmation(result);
      setStage('code_input');
      setCode('');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('SMS send failed:', err);
      setError("We couldn't send the SMS. Please try again.");
    } finally {
      setSending(false);
    }
  }, [dialCode, phone]);

  const handleVerifyCode = useCallback(async () => {
    if (!confirmation || code.length < 6) return;
    setError('');
    setStage('verifying');
    try {
      const { priorAnonymousUid } = await verifyCodeAndLink(confirmation, code);
      priorAnonymousUidRef.current = priorAnonymousUid;
      try {
        await ensureContactForPhoneAuth();
      } catch (linkErr) {
        // Phone link succeeded; contact callable can retry on resolve.
        // eslint-disable-next-line no-console
        console.warn('ensureContactForPhoneAuth failed (non-blocking):', linkErr);
      }
      const user = getFirebaseAuth().currentUser;
      if (user && (await continueIfReturning(user.uid))) {
        onVerified(priorAnonymousUid ? { priorAnonymousUid } : undefined);
      } else {
        setStage('terms');
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Code verification failed:', err);
      const code =
        err instanceof PhoneAuthError ? err.code : 'auth/invalid-verification-code';
      setError(describePhoneAuthError(code));
      setStage('code_input');
    }
  }, [confirmation, code, onVerified]);

  // Always-mounted off-screen reCAPTCHA host. Same off-screen
  // positioning trick capture uses — the verifier needs a non-zero
  // bounding rect so display:none is unsafe.
  const RecaptchaSlot = (
    <div
      id={RECAPTCHA_CONTAINER_ID}
      style={{
        position: 'absolute',
        top: '-9999px',
        left: '-9999px',
        width: '1px',
        height: '1px',
        overflow: 'hidden',
      }}
    />
  );

  if (stage === 'verifying') {
    return (
      <>
        {RecaptchaSlot}
        <LoadingScreen label={`Verifying ${dialCode} ${phone}…`} />
      </>
    );
  }

  if (stage === 'terms') {
    return (
      <TermsGate
        eyebrow="Verify"
        onAccepted={() => {
          const prior = priorAnonymousUidRef.current;
          onVerified(prior ? { priorAnonymousUid: prior } : undefined);
        }}
        onDeclined={() => {
          priorAnonymousUidRef.current = undefined;
          void ensureSessionReady();
          setStage('phone_input');
        }}
      />
    );
  }

  // Shared chrome: header + logo + heading reused across the two
  // form sub-states so the layout doesn't reflow when sub-state
  // changes.
  const Header = (
    <div className="mx-auto flex w-[min(17vw,4.2rem)] flex-col items-center text-x9-marine">
      <X9Logo title="X9" className="block w-full select-none" />
    </div>
  );

  const isPostAccept = mode === 'post_accept';
  const introTitle = isPostAccept ? 'Save this task to your account' : 'Verify your phone';
  const introBody = isPostAccept
    ? 'Verify your phone so this task shows up in the X9 app under Assigned to me. You can skip, but the task won’t be linked to you.'
    : 'This task is private. We’ll send a 6-digit code to make sure the link reached the right person.';

  return (
    <div className="page-shell">
      {RecaptchaSlot}
      <header className="page-header !pb-1">
        <span className="text-[10px] font-medium uppercase tracking-[0.2em] text-x9-marineSoft">
          Verify
        </span>
      </header>
      <div className="page-body text-center">
        {Header}

        {stage === 'phone_input' && (
          <>
            <h1 className="mt-7 text-[28px] font-bold tracking-tight text-x9-ink">
              {introTitle}
            </h1>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-x9-marineSoft">
              {introBody}
            </p>

            <form
              id="execute-phone-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (!sending) void handleSendCode();
              }}
              className="mx-auto mt-8 flex w-full max-w-md flex-col gap-4 text-left"
            >
              <div>
                <label
                  htmlFor="phone"
                  className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-x9-marineSoft"
                >
                  Phone number
                </label>
                <div className="flex gap-2">
                  <select
                    value={dialCode}
                    onChange={(e) => setDialCode(e.target.value)}
                    disabled={sending}
                    className="field-input-paper w-28 shrink-0 appearance-none p-3 text-center"
                    aria-label="Country code"
                  >
                    {COUNTRY_OPTIONS.map((c) => (
                      <option
                        key={c.code}
                        value={c.dial}
                        className="bg-white text-x9-ink"
                      >
                        {c.dial} {c.code}
                      </option>
                    ))}
                  </select>
                  <input
                    id="phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="Phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={sending}
                    autoFocus
                    className="field-input-paper flex-1 p-3"
                  />
                </div>
              </div>

              {error && (
                <p className="text-sm text-x9-error">{error}</p>
              )}
            </form>
          </>
        )}

        {stage === 'code_input' && (
          <>
            <h1 className="mt-7 text-[28px] font-bold tracking-tight text-x9-ink">
              Enter the code
            </h1>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-x9-marineSoft">
              We sent a 6-digit code to {dialCode} {phone}.
            </p>

            <form
              id="execute-otp-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (code.length === 6) void handleVerifyCode();
              }}
              className="mx-auto mt-8 flex w-full max-w-md flex-col gap-4 text-left"
            >
              <div>
                <label
                  htmlFor="code"
                  className="mb-2 block text-xs font-medium uppercase tracking-[0.18em] text-x9-marineSoft"
                >
                  Verification code
                </label>
                <input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  autoFocus
                  className="field-input-paper p-3 text-center text-2xl tracking-[0.5em]"
                />
              </div>

              {error && (
                <p className="text-sm text-x9-error">{error}</p>
              )}
            </form>

            <button
              type="button"
              onClick={() => {
                setStage('phone_input');
                setError('');
                setCode('');
              }}
              className="mt-4 text-sm font-medium text-x9-marineSoft underline-offset-2 hover:text-x9-ink hover:underline"
            >
              Use a different number
            </button>
          </>
        )}
      </div>

      {stage === 'phone_input' && (
        <FloatingFooter>
          {isPostAccept && onSkip ? (
            <FloatingFooterNavButton label="Skip" onClick={onSkip} />
          ) : null}
          <FloatingFooterCta
            type="submit"
            form="execute-phone-form"
            disabled={sending || !phone.trim()}
            aria-busy={sending}
          >
            {sending ? 'Sending…' : 'Send code'}
          </FloatingFooterCta>
        </FloatingFooter>
      )}

      {stage === 'code_input' && (
        <FloatingFooter>
          <FloatingFooterNavButton
            label="Back"
            onClick={() => {
              setStage('phone_input');
              setError('');
              setCode('');
            }}
          />
          <FloatingFooterCta
            type="submit"
            form="execute-otp-form"
            disabled={code.length !== 6}
          >
            Verify
          </FloatingFooterCta>
        </FloatingFooter>
      )}
    </div>
  );
}
